import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaCalendar,
  FaUser,
  FaArrowRight,
  FaSearch,
  FaEnvelope,
  FaBookOpen,
  FaLeaf,
  FaEye,
  FaSpinner,
  FaHeart,
  FaUtensils,
  FaPepperHot,
  FaClock,
} from "react-icons/fa";
import { blogAPI } from "../config/api";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Fetch blog posts from API
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log("🔵 Fetching blog posts...");
        const response = await blogAPI.getAll();
        console.log("✅ Blog posts fetched:", response.data);

        // Filter only published posts
        const publishedPosts = response.data.filter(
          (post) => post.status === "published"
        );
        setPosts(publishedPosts);
      } catch (err) {
        console.error("❌ Error fetching posts:", err);
        setError(err.response?.data?.message || "Failed to load blog posts");
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Get unique categories from posts
  const categories = [
    "All",
    ...new Set(posts.map((post) => post.category).filter(Boolean)),
  ];

  // Filter posts based on search and category
  const filteredPosts = posts.filter((post) => {
    const matchesSearch =
      post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.content?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      Sustainability:
        "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      "Health & Nutrition":
        "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      Recipes:
        "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
      Spices:
        "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
      default:
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
    };
    return colors[category] || colors.default;
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const icons = {
      Sustainability: FaLeaf,
      "Health & Nutrition": FaHeart,
      Recipes: FaUtensils,
      Spices: FaPepperHot,
    };
    return icons[category] || FaBookOpen;
  };

  // Get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return `https://worganaturals.com${imagePath}`;
  };

  // Animated fruit emojis for background
  const floatingFruits = [
    { emoji: "🍌", delay: 0, duration: 20, x: "10%", y: "15%" },
    { emoji: "🥭", delay: 2, duration: 25, x: "80%", y: "20%" },
    { emoji: "🍍", delay: 4, duration: 22, x: "15%", y: "70%" },
    { emoji: "🥥", delay: 1, duration: 23, x: "85%", y: "75%" },
    { emoji: "🍌", delay: 3, duration: 24, x: "50%", y: "10%" },
    { emoji: "🥭", delay: 5, duration: 21, x: "30%", y: "50%" },
    { emoji: "🍍", delay: 2.5, duration: 26, x: "70%", y: "50%" },
    { emoji: "🥥", delay: 4.5, duration: 19, x: "90%", y: "40%" },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-dark-900 dark:to-dark-800">
        <div className="text-center">
          <FaSpinner className="animate-spin text-6xl text-green-600 dark:text-green-400 mx-auto mb-4" />
          <p className="text-xl text-gray-600 dark:text-gray-300">
            Loading blog posts...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 dark:from-dark-900 dark:to-dark-800">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-green-100 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 relative overflow-hidden">
      {/* Animated Wave Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <svg
          className="absolute w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="wave-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop
                offset="0%"
                style={{ stopColor: "#10b981", stopOpacity: 0.2 }}
              />
              <stop
                offset="50%"
                style={{ stopColor: "#3b82f6", stopOpacity: 0.2 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: "#10b981", stopOpacity: 0.2 }}
              />
            </linearGradient>
          </defs>
          <motion.path
            d="M0,100 Q250,50 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z"
            fill="url(#wave-gradient)"
            animate={{
              d: [
                "M0,100 Q250,50 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z",
                "M0,100 Q250,150 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z",
                "M0,100 Q250,50 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z",
              ],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </svg>
      </div>

      {/* Animated Fruit Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingFruits.map((fruit, index) => (
          <motion.div
            key={index}
            className="absolute"
            style={{ left: fruit.x, top: fruit.y }}
            animate={{
              y: [0, -40, 0],
              x: [0, 20, 0],
              rotate: [0, 360],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: fruit.duration,
              repeat: Infinity,
              delay: fruit.delay,
              ease: "easeInOut",
            }}
          >
            <div className="text-6xl md:text-8xl opacity-20 drop-shadow-lg">
              {fruit.emoji}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Misty Overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-white/30 dark:from-dark-900/50 dark:via-transparent dark:to-dark-900/30"
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="pt-32 pb-20 px-6 md:px-12 lg:px-20"
        >
          <div className="container mx-auto max-w-7xl">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="flex items-center justify-center mb-6">
                <FaBookOpen className="text-5xl md:text-6xl text-green-600 dark:text-green-400 mr-4" />
                <h1 className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white">
                  Blog Insights
                </h1>
              </div>
              <p className="text-xl md:text-2xl mb-12 text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                Discover stories, recipes, and insights about organic living and
                sustainable farming
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <FaSearch className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
                  <input
                    type="text"
                    placeholder="Search blog posts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-16 pr-6 py-5 rounded-full text-gray-800 dark:text-white bg-white/90 dark:bg-dark-700/90 backdrop-blur-sm border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-4 focus:ring-green-300 dark:focus:ring-green-600 shadow-2xl transition-all"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Category Filter */}
        <section className="py-8 px-6 md:px-12 lg:px-20 sticky top-0 z-40 backdrop-blur-md bg-white/80 dark:bg-dark-800/80 shadow-lg">
          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category, index) => {
                const CategoryIcon = getCategoryIcon(category);
                return (
                  <motion.button
                    key={category}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedCategory(category)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all shadow-lg ${
                      selectedCategory === category
                        ? "bg-gradient-to-r from-green-600 to-blue-600 text-white"
                        : "bg-white dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-600"
                    }`}
                  >
                    <CategoryIcon className="text-lg" />
                    {category}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-20 px-6 md:px-12 lg:px-20">
          <div className="container mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Showing{" "}
                <span className="font-bold text-green-600 dark:text-green-400">
                  {filteredPosts.length}
                </span>{" "}
                of <span className="font-bold">{posts.length}</span> posts
              </p>
            </div>

            {filteredPosts.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-20"
              >
                <FaSearch className="text-6xl text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2">
                  No posts found
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Try adjusting your search or filter criteria
                </p>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <AnimatePresence>
                  {filteredPosts.map((post, index) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1 }}
                      className="group"
                    >
                      <Link to={`/blog/${post.slug}`}>
                        <div className="bg-white dark:bg-dark-700 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 backdrop-blur-sm">
                          {/* Post Image */}
                          <div className="relative h-56 overflow-hidden bg-gradient-to-br from-green-400 to-blue-500">
                            {post.image ? (
                              <img
                                src={getImageUrl(post.image)}
                                alt={post.image_alt || post.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <FaLeaf className="text-white text-6xl opacity-50" />
                              </div>
                            )}

                            {/* Category Badge */}
                            {post.category && (
                              <div className="absolute top-4 left-4">
                                <span
                                  className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(
                                    post.category
                                  )}`}
                                >
                                  {post.emoji} {post.category}
                                </span>
                              </div>
                            )}

                            {/* Featured Badge */}
                            {post.featured === 1 && (
                              <div className="absolute top-4 right-4">
                                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-400 text-yellow-900">
                                  ⭐ Featured
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Post Content */}
                          <div className="p-6">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                              {post.title}
                            </h2>

                            <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                              {post.excerpt ||
                                (post.content
                                  ? post.content.substring(0, 150) + "..."
                                  : "")}
                            </p>

                            {/* Meta Information */}
                            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                              <div className="flex items-center gap-1">
                                <FaCalendar className="text-green-600 dark:text-green-400" />
                                <span>
                                  {formatDate(
                                    post.published_at || post.created_at
                                  )}
                                </span>
                              </div>
                              {post.read_time && (
                                <div className="flex items-center gap-1">
                                  <FaClock className="text-blue-600 dark:text-blue-400" />
                                  <span>{post.read_time}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <FaEye className="text-purple-600 dark:text-purple-400" />
                                <span>{post.views || 0}</span>
                              </div>
                            </div>

                            {/* Author & Read More */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
                              <div className="flex items-center gap-2">
                                <FaUser className="text-gray-400" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {post.author || "Anonymous"}
                                  </p>
                                  {post.author_role && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {post.author_role}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold group-hover:gap-3 transition-all">
                                <span>Read</span>
                                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Section */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-16 px-6 mx-6 md:mx-12 lg:mx-20 rounded-3xl shadow-2xl mb-20"
        >
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <FaEnvelope className="text-5xl mx-auto mb-6" />
            </motion.div>
            <h2 className="text-4xl font-bold mb-4">
              Subscribe to Our Newsletter
            </h2>
            <p className="text-xl text-green-100 mb-8">
              Get the latest blog posts and updates delivered to your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 rounded-xl text-gray-800 focus:ring-2 focus:ring-white focus:outline-none"
              />
              <button className="bg-white text-green-600 px-8 py-3 rounded-xl font-bold hover:bg-green-50 transition-all hover:scale-105">
                Subscribe
              </button>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Blog;
